/**
 * @file 图片上传
 * @author haoyan@baidu.com
*/

/* globals getApp */

import * as core from './core';
import { request } from './request';

/**
 * 异常信息
 */
const ERROR_EMUM = {
  MAX_FILE_SIZE: {
    msg: 'MAX_FILE_SIZE',
    toast: '图片过大'
  }
};
const trigger = (fn, ...args) => {
  if (typeof fn === 'function') {
    fn.apply(fn, args);
  }
};

/**
 * 选取图片
 * @param {Object} conf
 * @param {boolean} conf.multi 是否可选择多图
 * @param {number} conf.compress 压缩比例
 * @param {string} conf.accept 可上传文件类型
 * @param {Array} conf.sourceType 上传资源类型，默认为相册和相机
 * @param {Function} conf.onChoose 选择图片后回调
 */
const choose = conf => {
  return new Promise(resolve => {
    {
      // 构造<input type='file'>
      const el = document.createElement('input');
      el.setAttribute('style', 'display: none');
      el.setAttribute('type', 'file');
      if (conf.accept) {
        el.setAttribute('accept', conf.accept);
      }
      if (conf.multi) {
        el.setAttribute('multiple', conf.multi);
      }
      // 解决ios上，内存中input无法触发change事件
      const wrap = conf.el || document.querySelector('.law-upload');
      wrap.appendChild(el);
      el.onchange = function (e) {
        const tempFiles = Array.prototype.slice.call(e.target.files);
        trigger(conf.onChoose, tempFiles);
        resolve(tempFiles);
      };
      el.click();
    }
  });
};

/* eslint-disable fecs-camelcase */
const _upload = conf => {
  /* eslint-enable fecs-camelcase */
  return new Promise((resolve, reject) => {
    {
      const formData = new FormData();
      formData.append(conf.name, conf.file, conf.file.name);
      request({
        url: conf.url,
        data: formData,
        method: 'POST',
        loginNeeded: false
      }).then(resp => {
        resolve(resp.data.data);
      }).catch(err => {
        console.log(err);
        reject();
      });
    }
  });
};
const preview = (file, cb) => {
  return trigger(cb, file);
};

/**
 * 获取图片方向
 * @param {File} file 图片对象
 * @return {Object} Promise对象，包含图片方向值
 */
const getOrientation = file => {
  /* eslint-disable */
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = e => {
      let view = new DataView(e.target.result);
      if (view.getUint16(0, false) != 0xFFD8) {
        resolve(-2);
      }
      let length = view.byteLength;
      let offset = 2;
      while (offset < length) {
        let marker = view.getUint16(offset, false);
        offset += 2;
        if (marker == 0xFFE1) {
          if (view.getUint32(offset += 2, false) != 0x45786966) {
            resolve(-1);
          }
          let little = view.getUint16(offset += 6, false) == 0x4949;
          offset += view.getUint32(offset + 4, little);
          let tags = view.getUint16(offset, little);
          offset += 2;
          for (var i = 0; i < tags; i++) {
            if (view.getUint16(offset + i * 12, little) == 0x0112) {
              resolve(view.getUint16(offset + i * 12 + 8, little));
            }
          }
        } else if ((marker & 0xFF00) != 0xFF00) {
          break;
        } else {
          offset += view.getUint16(offset, false);
        }
      }
      resolve(-1);
    };
  });
  /* eslint-enable */
};

/**
 * File转换为Base64
 * @param {File} file对象
 * @return {string} base64
 */
const fileToBase64 = file => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = e => {
      resolve(e.target.result);
    };
  });
};

/**
 * 获取压缩比
 * @param {File} file对象
 * @param {boolean} isCompress 是否进行压缩
 */
const getRatio = (file, isCompress) => {
  let ratio = 1;
  if (isCompress) {
    // 压缩比例区间
    if (file.size >= 7 * 1024 * 1024) {
      ratio = 0.2;
    } else if (file.size >= 6 * 1024 * 1024) {
      ratio = 0.4;
    } else if (file.size >= 3 * 1024 * 1024) {
      ratio = 0.5;
    } else if (file.size >= 400 * 1024) {
      ratio = 0.9;
    } else {
      ratio = 1;
    }
  }
  return ratio;
};

/**
 * Base64转Blob
 * @param {string} base64
 * @return {Blob} Blob对象
 */
const base64ToBlob = base64 => {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  let u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], {
    type: mime
  });
};

/**
 * @param {Object} userConf 用户配置
 * @param {string} userConf.url  // 上传地址
 * @param {boolean} userConf.compress // 是否压缩
 * @param {number} userConf.maxFileSize // 最大图片限制
 */
export const upload = userConf => {
  let conf = core.extend({
    url: '',
    el: null,
    compress: true,
    multi: false,
    name: 'picFile',
    maxFileSize: 20 * 1024 * 1024,
    // 20M
    accept: 'image/*',
    onChoose: null,
    onPreview: null,
    onUploadSuccess: null,
    onUploadError: null
  }, userConf);
  const afterChoose = info => {
    preview(info, conf.onPreview);
    _upload({
      url: conf.url,
      name: conf.name,
      file: info.file
    }).then(resp => {
      trigger(conf.onUploadSuccess, {
        resp,
        file: info
      });
    }).catch(resp => {
      trigger(conf.onUploadError, {
        resp,
        file: info
      });
    });
  };
  choose({
    el: conf.el,
    multi: conf.multi,
    compress: conf.compress,
    accept: conf.accept,
    sourceType: ['album', 'camera'],
    onChoose: conf.onChoose
  }).then(files => {
    files.forEach(file => {
      // 旋转图片（ios竖屏拍照上传图片会旋转90度，需要处理）
      getOrientation(file).then(orient => {
        // 转base64
        fileToBase64(file).then(base64 => {
          let ratio = conf.compress ? getRatio(file, conf.isCompress) : 1;
          const img = new Image();
          img.src = base64;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // 获取图片宽高
            let width = img.width;
            let height = img.height;
            let nbase64 = base64;
            let nfile = file;

            // 旋转图片
            if ([5, 6, 7, 8].indexOf(orient) > -1) {
              canvas.width = height;
              canvas.height = width;
            } else {
              canvas.width = width;
              canvas.height = height;
            }

            // transform
            switch (orient) {
              case 2:
                ctx.transform(-1, 0, 0, 1, width, 0);
                break;
              case 3:
                ctx.transform(-1, 0, 0, -1, width, height);
                break;
              case 4:
                ctx.transform(1, 0, 0, -1, 0, height);
                break;
              case 5:
                ctx.transform(0, 1, 1, 0, 0, 0);
                break;
              case 6:
                ctx.transform(0, 1, -1, 0, height, 0);
                break;
              case 7:
                ctx.transform(0, -1, -1, 0, height, width);
                break;
              case 8:
                ctx.transform(0, -1, 1, 0, 0, width);
                break;
              default:
                ctx.transform(1, 0, 0, 1, 0, 0);
            }
            ctx.drawImage(img, 0, 0);

            // 压缩图片
            if (ratio === 1) {
              nbase64 = canvas.toDataURL(file.type);
            } else if (ratio < 1) {
              nbase64 = canvas.toDataURL(file.type, ratio);
            }
            // 转为blob
            const blob = base64ToBlob(nbase64);
            // ios11.4以下不支持new File()，故使用blob上传
            blob.lastModifiedDate = new Date();
            blob.name = file.name;
            nfile = blob;
            if (nfile.size <= conf.maxFileSize) {
              afterChoose({
                file: nfile,
                base64: nbase64,
                upid: `pic_${core.rand()}`
              });
            } else {
              trigger(conf.onUploadError, ERROR_EMUM.MAX_FILE_SIZE);
            }
          };
        });
      });
    });
  });
};